import {
  ExtendedLayoutConfig,
  SavedView,
  Workspace,
  WorkspaceSummary,
} from '@helios/state-management'
import { AgGridState } from '@northfork/ag-grid-angular'

export interface WorkspaceUpdate extends Partial<Workspace> {
  //updatable fields
  name?: string
  description?: string
  workspace?: ExtendedLayoutConfig
}

export interface SavedViewUpdate extends Partial<SavedView> {
  //updatable fields
  savedView?: AgGridState
}

/**
 * Must be an abstract class due to angular injection.
 */
export abstract class WorkspaceService {
  /**
   * Returns a list of summaries of all of the user's custom workspaces
   */
  abstract getUserWorkspaceSummaries(): Promise<WorkspaceSummary[]>

  /**
   * Get a workspace summary given it's prefix
   *
   * @param workspacePrefix
   */
  abstract getWorkspaceSummary(
    workspacePrefix: string,
  ): Promise<WorkspaceSummary | undefined>

  /**
   * Get a workspace given it's prefix
   *
   * @param workspacePrefix
   */
  abstract getWorkspace(workspacePrefix: string): Promise<Workspace | undefined>

  /**
   * Gets the user's default workspace prefix
   */
  abstract getDefaultWorkspacePrefix(): Promise<string | undefined>

  /**
   * Gets the user's default workspace
   */
  abstract getDefaultWorkspace(): Promise<Workspace | undefined>

  /**
   * Creates a workspace given a Workspace.
   * The UUID will be replaced with a real one generated by the backend and the actual UUID will be returned.
   *
   * @param workspace
   */
  abstract createWorkspace(workspace: Workspace): Promise<string>

  /**
   * Given a workspacePrefix it will make that workspace the new default.
   *
   * @param workspacePrefix
   */
  abstract makeWorkspaceDefault(workspacePrefix: string): Promise<void>

  /**
   * Updates a workspace.
   *
   * @param workspaceUpdate
   */
  abstract updateWorkspace(
    workspacePrefix: string,
    workspaceUpdate: WorkspaceUpdate,
  ): Promise<void>

  /**
   * Returns a list of summaries of all of the preset workspaces
   */
  abstract getPresetWorkspaceSummaries(): Promise<WorkspaceSummary[]>

  /**
   * Clones the workspace and returns the workspacePrefix of the new workspace.
   *
   * @param workspacePrefix
   */
  abstract cloneWorkspace(
    workspacePrefix: string,
    newName?: string,
    newDescription?: string,
  ): Promise<string | undefined>

  /**
   * Deletes a workspace
   *
   * @param workspacePrefix
   */
  abstract deleteWorkspace(workspacePrefix: string): Promise<void>

  /**
   * Get a saved view given a workspace id and grid key
   *
   * @param workspacePrefix
   * @param gridKey
   */
  abstract getSavedViews(workspacePrefix: string): Promise<SavedView[]>

  /**
   * Updates a saved view
   *
   * @param update
   */
  abstract updateSavedView(
    savedViewPrefix: string,
    update: SavedViewUpdate,
  ): Promise<void>

  /**
   * Creates a saved view under the given workspace
   *
   * @param workspacePrefix
   * @param savedView
   */
  abstract createSavedView(
    workspacePrefix: string,
    gridKey: string,
    savedView: AgGridState,
    isPersistent: boolean,
  ): Promise<string>
}
